// src/pages/hospitals/HospitalsFilterSheet.jsx
import React from "react";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Sheet from "@/sot/primitives/Sheet.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";

const DATA_OPTIONS = [
  { id: "best-rating", label: "Best rating", icon: "/icons/patient/star_outline.svg" },
  { id: "near-you", label: "Near you", icon: "/icons/patient/distance.svg" },
];

const PAYMENT_OPTIONS = [
  { id: "cards", label: "Credit & Debit cards", icon: "/icons/doctor/credit_card.svg" },
  { id: "bank-transfer", label: "Bank transfers", icon: "/icons/doctor/account_balance.svg" },
  { id: "ewallet", label: "E-wallets", icon: "/icons/doctor/mobile_wallet.svg" },
  { id: "instant-eft", label: "Instant EFT", icon: "/icons/doctor/account_balance.svg" },
];

const ICON_CHECK = "/icons/patient/check_small.svg";

function toggleInArray(arr, id) {
  if (!Array.isArray(arr)) arr = [];
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

function FilterRow({ icon, label, checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="
        w-full
        flex items-center justify-between
        px-4 h-14
        bg-[var(--Schemes-Surface,#FFFFFF)]
      "
    >
      <div className="flex items-center gap-3">
        <span className="inline-flex h-6 w-6 items-center justify-center">
          <img src={icon} alt="" className="h-6 w-6" draggable="false" />
        </span>
        <span className="text-body-large text-on-surface text-left">
          {label}
        </span>
      </div>

      {/* Visual-only checkbox – state comes from row click */}
      <div className="pointer-events-none">
        <Checkbox checked={checked} />
      </div>
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h3 className="px-2 mb-2 text-title-medium text-on-surface-var">
        {title}
      </h3>
      <div
        className="
          rounded-[12px]
          overflow-hidden
          bg-[var(--Schemes-Surface,#FFFFFF)]
          shadow-[0_1px_2px_rgba(15,23,42,0.08)]
        "
      >
        {children}
      </div>
    </section>
  );
}

export default function HospitalsFilterSheet({ open, value, onChange, onClose }) {
  const [draft, setDraft] = React.useState(
    value || { data: [], payment: [] },
  );

  React.useEffect(() => {
    if (open) {
      setDraft(value || { data: [], payment: [] });
    }
  }, [open, value]);

  if (!open) return null;

  const apply = () => {
    onChange?.(draft);
    onClose?.();
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <div
        className="
          -mx-4 -mt-3
          rounded-t-[24px]
          bg-[rgb(var(--clr-surface-container-low))]
          pt-2 pb-4
        "
      >
        {/* drag handle */}
        <div className="flex justify-center pt-1 pb-1">
          <div className="h-1.5 w-10 rounded-full bg-[rgba(24,50,119,0.16)]" />
        </div>

        {/* Title row – TopBarCenter for centred title + X */}
        <div className="px-4 mb-2">
          <TopBarCenter
            title="Filter"
            close
            onBack={onClose}
            margin="mb-0"
          />
        </div>

        {/* Scrollable content */}
        <div className="px-4 max-h-[60vh] overflow-y-auto">
          {/* Data */}
          <Section title="Data">
            {DATA_OPTIONS.map((opt, idx) => (
              <React.Fragment key={opt.id}>
                <FilterRow
                  icon={opt.icon}
                  label={opt.label}
                  checked={draft.data?.includes(opt.id)}
                  onToggle={() =>
                    setDraft((prev) => ({
                      ...prev,
                      data: toggleInArray(prev.data, opt.id),
                    }))
                  }
                />
                {idx < DATA_OPTIONS.length - 1 && (
                  <div className="h-px bg-[rgba(15,23,42,0.06)] mx-4" />
                )}
              </React.Fragment>
            ))}
          </Section>

          {/* Payment methods */}
          <Section title="Payment methods">
            {PAYMENT_OPTIONS.map((opt, idx) => (
              <React.Fragment key={opt.id}>
                <FilterRow
                  icon={opt.icon}
                  label={opt.label}
                  checked={draft.payment?.includes(opt.id)}
                  onToggle={() =>
                    setDraft((prev) => ({
                      ...prev,
                      payment: toggleInArray(prev.payment, opt.id),
                    }))
                  }
                />
                {idx < PAYMENT_OPTIONS.length - 1 && (
                  <div className="h-px bg-[rgba(15,23,42,0.06)] mx-4" />
                )}
              </React.Fragment>
            ))}
          </Section>
        </div>

        {/* CTA – AfriMed button, same pattern as StepDob (white text) */}
        <div className="px-4 pt-2">
          <Button fullWidth onClick={apply}>
            Apply
          </Button>
        </div>
      </div>
    </Sheet>
  );
}